import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from 'recharts'; // still usable as a wrapper

// import { chartTimePeriod } from '../../../config/enum';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import type { RootState } from '../../../../redux/store';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
// import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import stationManagement from '../../../../utils/api/station-management';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

interface ChartDataProperties {
  label: string; // month/day/hour
  totalRevenue: number;
  totalAmountPaid: number;
  totalPayableAmount: number;
  monthName: string;
}
/**
 * Fill missing last 24 hrs generic logic
 * @param {any[]} data the array
 * @returns {any[]} The array with filled 24 hrs generic
 */
const fillMissingLast24HrsGeneric = (data: any[]) => {
  const result: any[] = [];
  const now = new Date();

  for (let index = 23; index >= 0; index -= 1) {
    const date = new Date(now);
    date.setHours(now.getHours() - index);

    const hour = date.getHours();
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    const found = data.find(
      (d) => d.hour === hour && d.day === day && d.month === month && d.year === year,
    );

    result.push({
      label: String(hour),
      year,
      month,
      day,
      stationDetails: found?.stationDetails ?? [],
    });
  }

  return result;
};

/**
 * Uptime TrendOver Time Chart
 * @returns {any} The rendered component
 */
function UptimeTrendOverTimeChart() {
  const { t } = useTranslation('sidePanel');
  const { selectedStationIds } = useAppSelector((state) => state.stationComparison);
  const dispatch = useAppDispatch();
  const [graphData, setGraphData] = useState<ChartDataProperties[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const [loading, setLoading] = useState<boolean>(false);
  //    const [graphData, setGraphData] = useState<(Partial<ChartDataProperties> & { label: string })[]>(
  //     [],
  //   );
  const chargingStationList = useAppSelector(
    (state: RootState) => state.stationManagement.chargingStationList,
  );
  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          /**
           * hides all legend items (no “radio buttons”)
           * @returns {any} \
           */
          filter: () => false,
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
      },
    },
    interaction: {
      intersect: false,
    },
    scales: {
      x: {
        grid: { display: false },
        border: { display: false },
      },
      y: {
        grid: { display: false }, //  remove Y grid lines
        border: { display: false }, //  remove Y axis line
        // ticks: { display: false }, //  remove Y axis numbers
        ticks: {
          display: true, // SHOW ticks
          count: 5,
          /**
           * Showing value with Hrs
           * @param {any} value The tick value
           * @returns {string} The formatted tick
           */
          callback: (value) => `${value} ${t('hrs')}`, // FORMAT ticks
          color: '#6b7280', // optional: nice gray
          font: {
            size: 11,
            weight: 500,
          },
        },
      },
    },
  };

  const stationIdsToUse = useMemo(() => {
    const selected = selectedStationIds.filter(Boolean);

    // If user selected stations → use them
    if (selected.length > 0) {
      return selected;
    }

    // Otherwise → use all charging stations
    return chargingStationList.map((station) => station._id);
  }, [selectedStationIds, chargingStationList]);

  /**
   * Fetches the revenue vs payable report data for the specified time range and updates the chart.
   * @param {any} range - The time period for the report ('last24hours', 'last7days', 'lastMonth', or 'last12months').
   */
  const fetchReport = async (range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months') => {
    if (chargingStationList.length === 0) return;

    setLoading(true);
    const chargingStationsToSend = stationIdsToUse.length > 1 ? 'all' : stationIdsToUse;
    const payload =
      chargingStationsToSend === 'all'
        ? {
            allStationList: true,
            timePeriod: range,
          }
        : {
            chargingStations: chargingStationsToSend,
            timePeriod: range,
          };
    try {
      const response = await stationManagement.fetchUptimeOverTimeChartData(payload);
      if (response.status === 200) {
        const { data } = response;

        let filledData: any[] = [];
        switch (range) {
          case 'last12months':
            filledData = fillMissingMonthsGeneric(data);
            break;
          case 'lastMonth':
            filledData = fillMissingLastNDaysGeneric(data, 30);
            break;
          case 'last7days':
            filledData = fillMissingLastNDaysGeneric(data, 7);
            break;
          case 'last24hours':
            filledData = fillMissingLast24HrsGeneric(data);
            break;
          default:
            filledData = data;
        }

        setGraphData(filledData);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  useEffect(() => {
    if (chargingStationList.length === 0) return;
    // Use default range; dateRange can be integrated later
    fetchReport('last24hours');
  }, [stationIdsToUse]);

  const labels = graphData.map((d) => d.label || d.monthName);

  // Extract all unique station IDs dynamically
  // Extract all unique station names
  const allStations = Array.from(
    new Set(graphData.flatMap((d: any) => (d.stationDetails || []).map((s: any) => s.stationName))),
  );

  const colorPalette = ['#00b894', '#6c5ce7', '#d63031', '#0984e3'];

  const datasets = allStations.map((stationName, index) => ({
    label: stationName,
    data: graphData.map((d: any) => {
      const station = d.stationDetails?.find((s: any) => s.stationName === stationName);
      return station?.totalHours ?? 0;
    }),
    borderColor: colorPalette[index % colorPalette.length],
    backgroundColor: `${colorPalette[index % colorPalette.length]}33`,
    fill: false,
    tension: 0.4,
    borderWidth: 1.5,
    pointRadius: 0,
    pointHoverRadius: 3,
  }));

  return (
    <div className="uptime-trend-over-time-chart">
      <div className="uptime-trend-over-time-container">
        <div className="uptime-trend-over-time-header">
          <div className="uptime-trend-over-time-title">{t('uptimeTrendOverTime')}</div>
          <CustomDateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={(start, end) => {
              setDateRange({ startDate: start, endDate: end });
              // Trigger fetch with default range; can be adapted to use dates
              fetchReport('last24hours');
            }}
            placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          />
        </div>
        <div className="uptime-trend-over-time-container">
          <div className="uptime-trend-over-time-chart">
            <ResponsiveContainer height={230}>
              {loading ? (
                <div className="loader">
                  <BatteryLoader />
                </div>
              ) : (
                <Line
                  data={{
                    labels,
                    datasets,
                  }}
                  options={chartOptions}
                />
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UptimeTrendOverTimeChart;
